import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { permissionsAndTabs, TABS, TabURI, validPermissions } from "../App";
import { useAuth } from "../hooks/useAuth";

export default function Home() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const actualTab = useMemo(
    () => location.pathname.slice(1),
    [location.pathname]
  );
  const tabs = useMemo(() => {
    // show tabs based on user permissions
    let _tabs: any[] = [];
    if (auth.isLogged) {
      let permissions: any[] = auth.user?.permissions ?? [];
      permissions = permissions.filter((permission) =>
        validPermissions.includes(permission)
      );
      const availableURIsForThesePermissions: any = new Set<string>();
      if (permissions && permissions.length > 0) {
        for (const permission of permissions) {
          if (
            permissionsAndTabs[permission] &&
            permissionsAndTabs[permission].tabs.length > 0
          ) {
            permissionsAndTabs[permission].tabs.forEach((element: TabURI) => {
              availableURIsForThesePermissions.add(element);
            });
          }
        }
      }
      let uris = Array.from(availableURIsForThesePermissions);

      if (uris.length === 0) {
        return _tabs;
      }

      let availableTabsForThesePermissions = TABS.filter((tab) =>
        uris.includes(tab.uri)
      );

      _tabs = availableTabsForThesePermissions.map((tab) => ({
        linkTo: tab.uri,
        title: tab.title,
      }));
    }
    return _tabs;
  }, [auth.isLogged, auth.user?.permissions]);

  const logoutHandler = useCallback(async () => {
    await auth.signOut!();
    navigate("/login");
  }, [auth.signOut, navigate]);

  return (
    <Box
      display={"flex"}
      width={"100%"}
      height={"100%"}
      overflow={"hidden"}
      gap={1}
      position={"relative"}
    >
      <Box
        boxShadow={5}
        bgcolor={"rgb(242, 242, 242)"}
        p={1}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box
            width={"100%"}
            justifyContent={"center"}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            pb={1}
          >
            <img
              alt="trendrating logo"
              src="./img/trendrating_icon_transparent_32x32_black.png"
              width={32}
              height={32}
            />
            <Typography sx={{ fontSize: "0.9vw" }}>
              <strong>Support</strong>
            </Typography>
          </Box>
          <Box
            boxShadow={3}
            py={1}
            borderRadius={1}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            justifyContent={"center"}
            title={`${auth.user?.firstName} ${auth.user?.lastName} (ID:${auth.user?.id})`}
          >
            <PersonIcon />
            <Typography>
              {`${auth.user?.firstName[0]}.`} {auth.user?.lastName}
            </Typography>
          </Box>
          <Tabs
            orientation="vertical"
            textColor="secondary"
            sx={{ display: "flex" }}
            indicatorColor="secondary"
            value={actualTab}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                value={tab.linkTo}
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  p: 1,
                  fontSize: "0.7vw",
                  alignItems: "baseline",
                  color: "secondary.main",
                  ":checked": "secondary.main",
                }}
                onClick={() => navigate(tab.linkTo)}
                label={tab.title}
              />
            ))}
          </Tabs>
        </Box>
        <Button onClick={logoutHandler} variant="text" color="secondary">
          <LogoutIcon />
          <Typography>Logout</Typography>
        </Button>
      </Box>
      <Box
        width={"100%"}
        display={"flex"}
        alignItems={"center"}
        py={1}
        px={1}
        overflow={"auto"}
      >
        {auth.isLogged && <Outlet />}
      </Box>
    </Box>
  );
}
